import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { readString } from 'react-papaparse';

import './Result.css';

import { ScatterPlot } from '../ScatterPlot/ScatterPlot';
import { ModalBox } from '../ModalBox/ModalBox';
import { ExplanationBox } from '../ExplanationBox/ExplanationBox';
import { Legend } from '../Legend/Legend';
import { Info } from '../Info/Info';
import { List } from '../List/List';
import { Loader } from '../Loader/Loader';
import { Error } from '../Error/Error';

import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import {
  formatText,
  convertCsvDataToJson,
  formatData,
} from '../../Utils/services';

import csvFile from '../../Data/data_v001.csv';

const Result = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stance, setStance] = useState('');
  const [initialData, setInitialData] = useState(null);
  const [data, setData] = useState(null);
  const [formattedData, setFormattedData] = useState(null);
  const [topic, setTopic] = useState('');
  const [query, setQuery] = useState('');
  const [stanceText, setStanceText] = useState('');
  const [open, setOpen] = useState(false);
  const [media, setMedia] = useState(null);
  const [view, setView] = useState('plot');
  const [mode, setMode] = useState('detailed');
  const [list, setList] = useState([]);
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [stanceExplanationOpen, setStanceExplanationOpen] = useState(false);

  useEffect(() => {
    readString(csvFile, {
      download: true,
      complete: (results) => {
        const data = convertCsvDataToJson(results);
        setInitialData(data);

        const queryParameters = new URLSearchParams(window.location.search);
        const language = queryParameters.get('language');
        const topic = queryParameters.get('topic');
        setTopic(formatText(topic, true));
        const query = queryParameters.get('query');
        setQuery(query);
        const stance = queryParameters.get('stance');
        setStance(stance);
        console.log('URL PARAMS', language, topic, query);

        let filteredData = data.filter((item) => {
          return (
            item.language === language &&
            item.topic === topic &&
            item.query === query
          );
        });
        console.log('FILTERED DATA BY LANGUAGE, TOPIC, & QUERY', filteredData);

        if (filteredData?.length && parseInt(stance) < 4) {
          setData(filteredData);
          const formattedData = formatData(filteredData, stance);
          setFormattedData(formattedData);

          const stance_text =
            stance === '0'
              ? 'No opinion'
              : filteredData[0]['stance_' + stance + '_text'];
          setStanceText(stance_text);

          const view = queryParameters.get('visualisation_type');
          if (view === 'list') {
            setMode('simple');
            setView(view);
            createListItems('simple', formattedData);
          } else {
            setMode('detailed');
          }

          setTimeout(() => {
            setLoading(false);
            setError(null);
          }, 1000);
        } else {
          setTimeout(() => {
            setLoading(false);
            setError('No data match the criteria');
          }, 1000);
        }
      },
      error: (error, file) => {
        console.log('ERROR PARSING CSV', error, file);
        setTimeout(() => {
          setLoading(false);
          setError('Error while parsing the csv file');
        }, 1000);
      },
    });
  }, []);

  const navigate = useNavigate();

  const handleClose = () => {
    setMedia(null);
    setOpen(false);
  };

  const handleMediaClick = (media) => {
    if (media) {
      setMedia(media);
      setOpen(true);
    }
  };

  const handleViewChange = (event, value) => {
    setView(value);
    if (value === 'list') {
      createListItems('detailed');
    }
  };

  const createListItems = (mode, data) => {
    let sortedData = null;
    if (data) {
      sortedData = data.sort((a, b) => Number(b.y) - Number(a.y));
    } else {
      sortedData = formattedData.sort((a, b) => Number(b.y) - Number(a.y));
    }
    const list = sortedData.map((item, i) => {
      return (
        <List
          key={'item' + i}
          item={item}
          mode={mode}
          handleMediaClick={handleMediaClick}
        ></List>
      );
    });
    setList(list);
  };

  const toggleExplanationModal = () => {
    setExplanationOpen(!explanationOpen);
  };

  const toggleStanceExplanationModal = () => {
    setStanceExplanationOpen(!stanceExplanationOpen);
  };

  return (
    <div className='results-container'>
      {mode === 'detailed' && (
        <div className='action-bar'>
          <Button variant='text' onClick={() => navigate('/')}>
            {'< '}Back
          </Button>
          <ToggleButtonGroup
            size='small'
            exclusive
            onChange={handleViewChange}
            value={view}
          >
            <ToggleButton value='list'>List</ToggleButton>
            <ToggleButton value='plot'>Plot</ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}
      {loading ? (
        <Loader />
      ) : error ? (
        <Error error={error} />
      ) : (
        <div className='result-container'>
          <div className='details-col left'>
            <Info topic={topic} query={query} stance={stanceText} mode={mode} />
            <div className='explanation-container'>
              <div
                className='explanation-row'
                onClick={() => toggleExplanationModal()}
              >
                How do we characterize the source?
              </div>
              <div
                className='explanation-row'
                onClick={() => toggleStanceExplanationModal()}
              >
                How do we calculate the stance?
              </div>
            </div>
            {/* {view === 'plot' && <Legend />} */}
            <Legend isListView={view === 'list'} />
          </div>
          {data && view === 'list' ? (
            <div className='list-container'>{list}</div>
          ) : (
            <>
              <div className='plot-container'>
                <ScatterPlot
                  data={formattedData}
                  initialData={initialData}
                  width={900}
                  height={500}
                  handleMediaClick={handleMediaClick}
                />
                <div className='plot-title top'>In favor</div>
                <div className='plot-title right'>Far right</div>
                <div className='plot-title bottom'>Against</div>
                <div className='plot-title left'>Far left</div>
              </div>
            </>
          )}
        </div>
      )}
      <ModalBox media={media} open={open} handleClose={handleClose}></ModalBox>
      <ExplanationBox
        open={explanationOpen}
        handleClose={toggleExplanationModal}
        type='source'
      ></ExplanationBox>
      <ExplanationBox
        open={stanceExplanationOpen}
        handleClose={toggleStanceExplanationModal}
        type='stance'
      ></ExplanationBox>
    </div>
  );
};

export default Result;
